import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import { generateQuestion } from '../gameEngine';
import { generateMCQuestion } from '../questionBanks';
import TeamPanel from '../components/game/TeamPanel';
import TugOfWarStage from '../components/game/TugOfWarStage';
import SoundToggle from '../components/game/SoundToggle';
import { motion, AnimatePresence } from 'framer-motion';
import {
  playCorrectSound, playWrongSound, playStreakSound,
  playSuperStreakSound, playCountdownSound, playGoSound,
  playWinSound, playTimerUrgentSound
} from '../soundEngine';

const WIN_THRESHOLD = 5;
const STREAK_BONUS_AT = 3;
const SUPER_STREAK_AT = 5;

// AI difficulty settings (delay in ms before AI answers)
const AI_SPEED = { easy: { min: 4000, max: 7000 }, medium: { min: 2500, max: 5000 }, hard: { min: 1500, max: 3500 } };
const AI_ACCURACY = { easy: 0.5, medium: 0.7, hard: 0.85 };

export default function SoloGamePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const settings = location.state;
  const lastUrgentRef = useRef(0);
  const aiTimerRef = useRef(null);

  const isMCQ = settings?.subjectType === 'mcq';
  const subjectId = settings?.subjectId || 'matematik';
  const gameLang = settings?.language || language;
  const difficulty = settings?.difficulty || 'easy';
  const playerName = settings?.playerName || 'Player';
  const aiQuestions = settings?.aiQuestions || [];
  const aiQueueRef = useRef([...aiQuestions]);

  const [countdown, setCountdown] = useState(3);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(settings?.duration || 60);
  const [gameOver, setGameOver] = useState(false);
  const [streakEffects, setStreakEffects] = useState([]);
  const [explanation, setExplanation] = useState(null);

  const [team1, setTeam1] = useState({
    question: null, input: '', score: 0, correct: 0, wrong: 0,
    feedback: null, streak: 0, bestStreak: 0, comboText: null, mcqSelected: null,
  });
  const [team2, setTeam2] = useState({
    question: null, input: '', score: 0, correct: 0, wrong: 0,
    feedback: null, streak: 0, bestStreak: 0, comboText: null, mcqSelected: null,
  });

  const genQuestion = useCallback(() => {
    if (aiQueueRef.current.length > 0) {
      const q = aiQueueRef.current.shift();
      return {
        q_en: q.q_en, q_bm: q.q_bm,
        options_en: q.options_en || q.options, options_bm: q.options_bm || q.options,
        answer: q.answer,
        explanation_en: q.explanation_en || '', explanation_bm: q.explanation_bm || '',
        isAI: true,
      };
    }
    if (isMCQ) return generateMCQuestion(subjectId, difficulty, gameLang);
    const totalCorrect = (team1?.correct || 0) + (team2?.correct || 0);
    return generateQuestion(difficulty, settings?.operations || ['+'], totalCorrect);
  }, [isMCQ, subjectId, settings, gameLang, difficulty, team1?.correct, team2?.correct]);

  useEffect(() => {
    if (!settings) { navigate('/setup'); return; }
    setTeam1(prev => ({ ...prev, question: genQuestion() }));
    setTeam2(prev => ({ ...prev, question: genQuestion() }));
  }, []);

  // Countdown
  useEffect(() => {
    if (countdown > 0) {
      playCountdownSound();
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !gameStarted) {
      playGoSound();
      setGameStarted(true);
    }
  }, [countdown, gameStarted]);

  // Timer
  useEffect(() => {
    if (gameStarted && !gameOver && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 11 && prev > 1) {
            const now = Date.now();
            if (now - lastUrgentRef.current > 900) { playTimerUrgentSound(); lastUrgentRef.current = now; }
          }
          if (prev <= 1) { setGameOver(true); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, gameOver, timeLeft]);

  const ropePosition = team1.score - team2.score;

  useEffect(() => {
    if (Math.abs(ropePosition) >= WIN_THRESHOLD && gameStarted) setGameOver(true);
  }, [ropePosition, gameStarted]);

  // Game over
  useEffect(() => {
    if (gameOver && settings) {
      playWinSound();
      if (aiTimerRef.current) clearTimeout(aiTimerRef.current);
      const timer = setTimeout(() => {
        let winner = 'draw';
        if (team1.score > team2.score) winner = 'team1';
        else if (team2.score > team1.score) winner = 'team2';
        navigate('/results', {
          state: {
            team1Name: playerName, team2Name: 'AI Bot',
            team1Score: team1.score, team2Score: team2.score,
            team1Correct: team1.correct, team1Wrong: team1.wrong,
            team2Correct: team2.correct, team2Wrong: team2.wrong,
            team1BestStreak: team1.bestStreak, team2BestStreak: team2.bestStreak,
            winner, difficulty, operations: settings.operations || [],
            duration: settings.duration, subjectId,
            subjectType: isMCQ ? 'mcq' : 'math',
            schoolName: '', language: gameLang, isSoloMode: true,
          }
        });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [gameOver]);

  const addStreakEffect = useCallback((team, text, color) => {
    const id = Date.now() + Math.random();
    setStreakEffects(prev => [...prev, { id, team, text, color }]);
    setTimeout(() => setStreakEffects(prev => prev.filter(e => e.id !== id)), 1500);
  }, []);

  const processAnswer = useCallback((team, isCorrect) => {
    const setter = team === 'team1' ? setTeam1 : setTeam2;
    const state = team === 'team1' ? team1 : team2;
    if (isCorrect) {
      const newStreak = state.streak + 1;
      let bonusPoints = 0, comboText = null;
      if (newStreak >= SUPER_STREAK_AT) {
        bonusPoints = 2; comboText = `SUPER STREAK! x${newStreak}`;
        playSuperStreakSound(); addStreakEffect(team, `SUPER x${newStreak}! +3`, '#ff6b00');
      } else if (newStreak >= STREAK_BONUS_AT) {
        bonusPoints = 1; comboText = `STREAK! x${newStreak}`;
        playStreakSound(); addStreakEffect(team, `STREAK x${newStreak}! +2`, '#22c55e');
      } else { if (team === 'team1') playCorrectSound(); }
      setter(prev => ({
        ...prev, score: prev.score + 1 + bonusPoints, correct: prev.correct + 1,
        input: '', feedback: 'correct', streak: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak), comboText,
        question: genQuestion(), mcqSelected: null,
      }));
    } else {
      if (team === 'team1') {
        playWrongSound();
        const q = state.question;
        if (q && (q.explanation_en || q.explanation_bm)) {
          const expText = gameLang === 'en' ? (q.explanation_en || q.explanation_bm) : (q.explanation_bm || q.explanation_en);
          setExplanation(expText);
          setTimeout(() => setExplanation(null), 2500);
        }
      }
      setter(prev => ({ ...prev, wrong: prev.wrong + 1, input: '', feedback: 'wrong', streak: 0, comboText: null, question: genQuestion() }));
    }
    setTimeout(() => { setter(prev => ({ ...prev, feedback: null, comboText: null, mcqSelected: null })); }, 600);
  }, [team1, team2, genQuestion, addStreakEffect]);

  // AI Bot Logic
  useEffect(() => {
    if (!gameStarted || gameOver || !team2.question) return;
    const speed = AI_SPEED[difficulty] || AI_SPEED.easy;
    const accuracy = AI_ACCURACY[difficulty] || 0.5;
    const delay = speed.min + Math.random() * (speed.max - speed.min);

    aiTimerRef.current = setTimeout(() => {
      const isCorrect = Math.random() < accuracy;
      processAnswer('team2', isCorrect);
    }, delay);

    return () => { if (aiTimerRef.current) clearTimeout(aiTimerRef.current); };
  }, [gameStarted, gameOver, team2.question, difficulty]);

  // Player handlers
  const handleMCQSelect = useCallback((team, selectedIndex) => {
    if (gameOver || !gameStarted || team !== 'team1') return;
    const state = team1;
    setTeam1(prev => ({ ...prev, mcqSelected: selectedIndex }));
    processAnswer('team1', selectedIndex === state.question.answer);
  }, [gameOver, gameStarted, team1, processAnswer]);

  const handleDigit = useCallback((team, digit) => {
    if (gameOver || !gameStarted || team !== 'team1') return;
    setTeam1(prev => ({ ...prev, input: prev.input.length < 4 ? prev.input + digit : prev.input }));
  }, [gameOver, gameStarted]);

  const handleClear = useCallback((team) => {
    if (team !== 'team1') return;
    setTeam1(prev => ({ ...prev, input: '' }));
  }, []);

  const handleSubmit = useCallback((team) => {
    if (gameOver || !gameStarted || team !== 'team1') return;
    if (team1.input === '') return;
    processAnswer('team1', team1.input === team1.question.answer.toString());
  }, [gameOver, gameStarted, team1, processAnswer]);

  if (!settings) return null;

  return (
    <div className="game-page">
      <SoundToggle className="game-sound-toggle" />
      <div className="solo-ai-badge" data-testid="solo-ai-badge">VS AI</div>
      <AnimatePresence>
        {countdown > 0 && (
          <motion.div className="countdown-overlay" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div key={countdown} className="countdown-number"
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 2, opacity: 0 }} transition={{ duration: 0.6 }}>
              {countdown}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {gameOver && (
          <motion.div className="gameover-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.div className="gameover-text" initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
              {timeLeft === 0 ? t('timesUp', language) : t('gameOver', language)}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {streakEffects.map(effect => (
          <motion.div key={effect.id} className={`streak-effect streak-effect-${effect.team}`}
            style={{ color: effect.color }}
            initial={{ opacity: 0, y: 20, scale: 0.5 }} animate={{ opacity: 1, y: -40, scale: 1.2 }}
            exit={{ opacity: 0, y: -80 }}>{effect.text}</motion.div>
        ))}
      </AnimatePresence>
      <div className="game-layout">
        <TeamPanel team="team1" teamName={playerName}
          question={team1.question} input={team1.input} feedback={team1.feedback}
          comboText={team1.comboText} streak={team1.streak}
          isMCQ={isMCQ} mcqSelectedIndex={team1.mcqSelected}
          onDigit={(d) => handleDigit('team1', d)} onClear={() => handleClear('team1')}
          onSubmit={() => handleSubmit('team1')}
          onMCQSelect={(idx) => handleMCQSelect('team1', idx)}
          disabled={!gameStarted || gameOver} language={language} />
        <TugOfWarStage ropePosition={ropePosition}
          team1Score={team1.score} team2Score={team2.score}
          team1Name={playerName} team2Name="AI Bot"
          timeLeft={timeLeft} maxPosition={WIN_THRESHOLD}
          gameOver={gameOver} language={language} />
        <TeamPanel team="team2" teamName="AI Bot"
          question={team2.question} input={''} feedback={team2.feedback}
          comboText={team2.comboText} streak={team2.streak}
          isMCQ={isMCQ} mcqSelectedIndex={null}
          onDigit={() => {}} onClear={() => {}} onSubmit={() => {}} onMCQSelect={() => {}}
          disabled={true} language={language} />
      </div>
    </div>
  );
}
